import type { TPermission } from '@/constants/permissions';

import { FOOTER_ROWS, GUARDED_ROUTES, NAVIGATION } from '@/constants/navigation';
import { GRANT_SETS } from '@/constants/permissions';

import type { TNavBadges, TNavSection, TPersona } from 'constants/navigation';

const ROOT_ROUTE = '/';

const hasPermission = (persona: TPersona | null, permission: TPermission): boolean => {
  if (!persona) {
    return false;
  }

  return GRANT_SETS[persona].has(permission);
};

const resolveNavigation = (persona: TPersona | null): TNavSection[] => {
  if (!persona) {
    return [];
  }

  return NAVIGATION
    .map(section => ({
      ...section,
      rows: section.rows.filter(row => !row.permission || hasPermission(persona, row.permission)),
    }))
    .filter(section => section.rows.length > 0);
};

const withNavBadges = (sections: TNavSection[], badges: TNavBadges): TNavSection[] => sections
  .map(section => ({
    ...section,
    rows: section.rows.map((row) => {
      const count = row.badgeKey ? badges[row.badgeKey] : undefined;

      if (!count) {
        return row;
      }

      return { ...row, count };
    }),
  }));

const resolveRoutePermission = (pathname: string): TPermission | null => {
  if (pathname === ROOT_ROUTE) {
    return null;
  }

  const rows = [...NAVIGATION.flatMap(section => section.rows), ...FOOTER_ROWS, ...GUARDED_ROUTES];

  const exact = rows.find(row => row.href === pathname);

  if (exact) {
    return exact.permission ?? null;
  }

  const nested = rows
    .filter(row => row.href && row.href !== ROOT_ROUTE && pathname.startsWith(`${row.href}/`))
    .sort((left, right) => (right.href?.length ?? 0) - (left.href?.length ?? 0));

  return nested[0]?.permission ?? null;
};

export {
  hasPermission,
  resolveNavigation,
  resolveRoutePermission,
  withNavBadges,
};
